/** @format */

// src/routes/AppRoutes.jsx

import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense } from 'react'
import { useAuth } from '@/entities/auth/model'
import PrivateRoute from '../PrivateRoutes'
import { ErrorFallback } from '@/shared/lib/error-boundary/error-fallback'
import { useAuthHandler } from './AuthManager'
import { useTranslationData } from './../../utils/SidebarData'
import { ErrorBoundary } from 'react-error-boundary'
import { Spinner } from '@/shared/ui/atoms/spinner'
import { Home, Login } from 'pages'
import NotFound from '@/features/not-found'
import { type RouteConfig, type PermissionMap } from '@/shared/config/route-config'
import { ParamRemount } from '@/shared/lib/param-remount'

const normalizePath = (path: string | undefined) =>
  path?.startsWith('/') ? path.slice(1) : path || ''

/** Extract the first dynamic param name from a route path, e.g. `:id` → `'id'` */
const extractFirstParam = (path?: string): string | null => {
  const match = path?.match(/:(\w+)/)
  // invariant: regex has a capturing group, so match[1] exists when match is non-null
  return match ? match[1]! : null
}

const renderDynamicRoutes = (routes: RouteConfig[], permission: PermissionMap) => {
  const result: React.ReactNode[] = []

  routes?.forEach(({ child, component: ChildComponent, hidden, name, pathname, id }) => {
    if (hidden && !permission[name]?.VIEW) return

    const hasChildren = child?.length > 0

    // Only render routes that have components
    // Parent route redirects are handled manually in AppRoutes
    if (ChildComponent) {
      const paramName = extractFirstParam(pathname)

      result.push(
        <Route
          key={id}
          path={normalizePath(pathname)}
          element={
            <PrivateRoute>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Suspense fallback={<Spinner />}>
                  {paramName ? (
                    <ParamRemount paramName={paramName} Component={ChildComponent} />
                  ) : (
                    <ChildComponent />
                  )}
                </Suspense>
              </ErrorBoundary>
            </PrivateRoute>
          }
        />,
      )
    }

    // Recursively process children as sibling routes (flattened)
    if (hasChildren) {
      result.push(...renderDynamicRoutes(child, permission))
    }
  })

  return result
}

const AppRoutes = () => {
  useAuthHandler()
  const routes = useTranslationData()
  const { permission } = useAuth()

  const dataLibraryList = routes.find((r) => r.name === 'DATA_LIBRARY')?.child ?? []
  const warehouseList = routes.find((r) => r.name === 'WAREHOUSE')?.child ?? []
  const supplyList = routes.find((r) => r.name === 'SUPPLY')?.child ?? []
  const incomingDocumentsList =
    routes.find((r) => r.name === 'REQUEST_ORDER_SIGNATURE')?.child ?? []
  const financeList = routes.find((r) => r.name === 'TRANSPORTATION')?.child ?? []
  const userList = routes.find((r) => r.name === 'USER')?.child ?? []
  const salesList = routes.find((r) => r.name === 'SALES')?.child ?? []
  const hrList = routes.find((r) => r.name === 'HR')?.child ?? []
  const productionList = routes.find((r) => r.name === 'PRODUCTION')?.child ?? []
  const gateList = routes.find((r) => r.name === 'GATE')?.child ?? []
  const accountingList = routes.find((r) => r.name === 'ACCOUNTING')?.child ?? []

  return (
    <Routes>
      <Route index path='/' element={<Home />} />
      <Route path='/login' element={<Login />} />

      {/* Manual redirect routes - BEFORE all dynamic routes */}
      {/* Specific paths must come before general paths */}
      <Route path='data-library' element={<Navigate to='/data-library/categories' replace />} />
      <Route path='warehouse' element={<Navigate to='/warehouse/stocks-management' replace />} />
      <Route
        path='warehouse/stocks-management/:id/incomes'
        element={<Navigate to='new-income' replace relative='route' />}
      />
      {/* supply/request-order must come before supply */}
      <Route
        path='supply/request-order'
        element={<Navigate to='/supply/request-order/new' replace />}
      />
      {/* bare transport-invoice → default to the table (/list) view */}
      <Route
        path='supply/transport-invoice'
        element={<Navigate to='/supply/transport-invoice/list' replace />}
      />
      <Route path='supply' element={<Navigate to='/supply/request-order/new' replace />} />
      <Route path='gate' element={<Navigate to='/gate/awaiting' replace />} />
      <Route path='accounting' element={<Navigate to='/accounting/dashboard' replace />} />
      <Route
        path='incoming-documents'
        element={<Navigate to='/incoming-documents/orders' replace />}
      />
      <Route path='finance' element={<Navigate to='/finance/pricing' replace />} />
      <Route path='sales' element={<Navigate to='/sales/orders/all' replace />} />
      <Route path='hr' element={<Navigate to='/hr/employees' replace />} />
      <Route path='production' element={<Navigate to='/production/planning' replace />} />
      <Route path='user' element={<Navigate to='/user/user-profile' replace />} />

      {dataLibraryList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <Route
            key={id}
            path={normalizePath(pathname)}
            element={
              <PrivateRoute>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <Suspense fallback={<Spinner />}>
                    <Component />
                  </Suspense>
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
        ),
      )}
      {warehouseList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {supplyList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : Component ? (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ) : (
          renderDynamicRoutes(child, permission)
        ),
      )}
      {gateList.map((route: RouteConfig) => {
        const { id, pathname, component: Component, hidden, name, child } = route
        if (hidden && !permission[name]?.VIEW) return null
        if (!Component) return renderDynamicRoutes(child, permission)
        return (
          <React.Fragment key={id}>
            <Route
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </React.Fragment>
        )
      })}
      {incomingDocumentsList.map(
        ({ id, pathname, component: Component, hidden, name, child }: any) =>
          hidden && !permission[name]?.VIEW ? (
            []
          ) : (
            <>
              <Route
                key={id}
                path={normalizePath(pathname)}
                element={
                  <PrivateRoute>
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      <Suspense fallback={<Spinner />}>
                        <Component />
                      </Suspense>
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              {renderDynamicRoutes(child, permission)}
            </>
          ),
      )}
      {financeList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {userList?.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {salesList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {hrList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {productionList.map(({ id, pathname, component: Component, hidden, name, child }: any) =>
        hidden && !permission[name]?.VIEW ? (
          []
        ) : (
          <>
            <Route
              key={id}
              path={normalizePath(pathname)}
              element={
                <PrivateRoute>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Suspense fallback={<Spinner />}>
                      <Component />
                    </Suspense>
                  </ErrorBoundary>
                </PrivateRoute>
              }
            />
            {renderDynamicRoutes(child, permission)}
          </>
        ),
      )}
      {accountingList.map(
        ({ id, pathname, component: Component, hidden, name, child }: RouteConfig) =>
          hidden && !permission[name]?.VIEW ? (
            []
          ) : (
            <>
              {Component && (
                <Route
                  key={id}
                  path={normalizePath(pathname)}
                  element={
                    <PrivateRoute>
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <Suspense fallback={<Spinner />}>
                          <Component />
                        </Suspense>
                      </ErrorBoundary>
                    </PrivateRoute>
                  }
                />
              )}
              {renderDynamicRoutes(child, permission)}
            </>
          ),
      )}

      <Route path='*' element={<NotFound />} />
    </Routes>
  )
}

export default AppRoutes
